import { BusinessHours } from '../types/shop';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

type DayName = typeof DAY_NAMES[number];

/** Parses a "HH:mm" (or "HH:mm:ss") string into minutes since midnight. */
const toMinutes = (time: string): number | null => {
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  return hours * 60 + minutes;
};

export interface OpenStatus {
  isOpen: boolean;
  /** Human-readable hours for the current/relevant day, e.g. "09:00 - 18:00". */
  todayHours: string | null;
}

/**
 * Computes whether a shop/barber is currently open by evaluating the given
 * weekly business hours (if available) or, failing that, the legacy
 * single opening/closing time strings, against the current client time.
 */
export const getOpenStatus = (
  businessHours?: BusinessHours,
  fallbackOpeningHours?: string,
  fallbackClosingHours?: string,
  now: Date = new Date()
): OpenStatus => {
  interface ParsedSchedule {
    startTime: string;
    endTime: string;
    startMinutes: number;
    endMinutes: number;
    /** True when the schedule's end time is on/before its start time, i.e. it spans midnight. */
    isOvernight: boolean;
  }

  /** Parses the schedule for `dayOffset` days relative to `now` (e.g. -1 for yesterday). */
  const parseDay = (dayOffset: number): ParsedSchedule | null => {
    const dayIndex = (((now.getDay() + dayOffset) % 7) + 7) % 7;
    const dayName = DAY_NAMES[dayIndex] as DayName;
    const schedule = businessHours?.businessHoursMap?.[dayName];

    const startTime = schedule?.startTime ?? fallbackOpeningHours;
    const endTime = schedule?.endTime ?? fallbackClosingHours;
    const open = schedule ? schedule.open : true;

    if (!startTime || !endTime || !open) return null;

    const startMinutes = toMinutes(startTime);
    const endMinutes = toMinutes(endTime);
    if (startMinutes === null || endMinutes === null) return null;

    return {
      startTime,
      endTime,
      startMinutes,
      endMinutes,
      isOvernight: endMinutes <= startMinutes,
    };
  };

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const today = parseDay(0);
  const yesterday = parseDay(-1);

  // Today's own schedule only ever covers times from its start time onward
  // (through midnight, for an overnight range). Explicitly reject treating
  // an overnight range as also covering the early hours *before* its start
  // time - that pre-start window belongs to yesterday's overnight schedule,
  // not today's, and is handled separately below.
  if (today) {
    const openToday = today.isOvernight
      ? nowMinutes >= today.startMinutes
      : nowMinutes >= today.startMinutes && nowMinutes < today.endMinutes;
    if (openToday) {
      return {
        isOpen: true,
        todayHours: `${today.startTime} - ${today.endTime}`,
      };
    }
  }

  // Yesterday's overnight schedule (e.g. 20:00 - 02:00) can still be open
  // into the small hours of today, e.g. still open at 01:00.
  if (yesterday?.isOvernight && nowMinutes < yesterday.endMinutes) {
    return {
      isOpen: true,
      todayHours: `${yesterday.startTime} - ${yesterday.endTime}`,
    };
  }

  return {
    isOpen: false,
    todayHours: today ? `${today.startTime} - ${today.endTime}` : null,
  };
};
